/* An Astro case study, edited in the same visual editor as a DNN one.
 *
 * The Astro file is front matter only: content_blocks is the article, one
 * block per section (case-studies/lede, takeaways, facts, content-section, cta,
 * faq), the same sections the DNN template has as tc-* divs. So the blocks are
 * drawn as that HTML, edited by HtmlDraft exactly as a DNN draft is, and read
 * back into the blocks on save. Each section carries its block's index, and
 * what it is read back into is a copy of that block: a field the editor does
 * not show (a button's link_type, the gallery's columns) survives the edit, and
 * a section dragged elsewhere takes its whole block with it.
 *
 * Fields the site renders through markdown-it (lede, section and CTA bodies,
 * FAQ answers) round-trip as Markdown; the rest are plain text there, so plain
 * text here. A block this does not know (the gallery grid, anything a client
 * added) is a locked box that can be moved but not edited, and goes back as it
 * was. Everything outside content_blocks is left exactly as written. */
import { parseDocument } from 'yaml';

type Block = Record<string, unknown> & { _name?: string };

const FRONT = /^(\s*---[ \t]*\r?\n)([\s\S]*?\r?\n)(---[ \t]*)(\r?\n[\s\S]*)?$/;

function split(md: string) {
  const m = FRONT.exec(md);
  return m ? { open: m[1], yaml: m[2], close: m[3], rest: m[4] ?? '' } : null;
}

/** The case study's blocks, or null when this is not a case-study file the editor can draw. */
export function caseStudyBlocks(md: string): Block[] | null {
  const f = split(md);
  if (!f) return null;
  const doc = parseDocument(f.yaml);
  if (doc.errors.length) return null;
  const blocks = (doc.toJS() as { content_blocks?: unknown } | null)?.content_blocks;
  if (!Array.isArray(blocks) || !blocks.some((b) => typeof b?._name === 'string' && b._name.startsWith('case-studies/'))) return null;
  return blocks as Block[];
}

/* ---- Markdown → HTML (the subset the generator writes) -------------------- */

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const escAttr = (s: string) => esc(s).replace(/"/g, '&quot;');
const str = (v: unknown) => (v == null ? '' : String(v));

function inline(md: string): string {
  // Raw HTML is allowed in these fields (markdown-it html: true), so a tag stays a tag.
  return md
    .replace(/&(?![a-z]+;|#\d+;)/gi, '&amp;')
    .replace(/<(?![a-z/!])/gi, '&lt;')
    .replace(/\\([*_[\]`\\])/g, (_, c: string) => `&#${c.charCodeAt(0)};`)
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/!\[([^\]]*)\]\(([^)\s]+)\)/g, (_, a: string, u: string) => `<img src="${u.replace(/"/g, '&quot;')}" alt="${a.replace(/"/g, '&quot;')}" />`)
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_, t: string, u: string) => `<a href="${u.replace(/"/g, '&quot;')}">${t}</a>`)
    .replace(/\*\*([^*]+)\*\*|__([^_]+)__/g, (_, a?: string, b?: string) => `<strong>${a ?? b}</strong>`)
    .replace(/\*([^*]+)\*|\b_([^_]+)_\b/g, (_, a?: string, b?: string) => `<em>${a ?? b}</em>`)
    .replace(/ {2,}\n|\n/g, (m) => (m === '\n' ? ' ' : '<br>'));
}

export function mdToHtml(md: string): string {
  return md.replace(/\r\n/g, '\n').trim().split(/\n{2,}/).filter((b) => b.trim()).map((block) => {
    const lines = block.split('\n');
    if (lines.every((l) => /^\s*[-*+]\s+/.test(l))) return `<ul>${lines.map((l) => `<li>${inline(l.replace(/^\s*[-*+]\s+/, ''))}</li>`).join('')}</ul>`;
    if (lines.every((l) => /^\s*\d+[.)]\s+/.test(l))) return `<ol>${lines.map((l) => `<li>${inline(l.replace(/^\s*\d+[.)]\s+/, ''))}</li>`).join('')}</ol>`;
    const h = /^(#{2,4})\s+(.*)$/.exec(block);
    if (h && lines.length === 1) return `<h${h[1].length}>${inline(h[2])}</h${h[1].length}>`;
    if (lines.every((l) => /^>\s?/.test(l))) return `<blockquote><p>${inline(lines.map((l) => l.replace(/^>\s?/, '')).join('\n'))}</p></blockquote>`;
    if (/^<[a-z][\s\S]*>$/i.test(block.trim())) return block.trim();
    return `<p>${inline(block)}</p>`;
  }).join('');
}

/* ---- HTML → Markdown ------------------------------------------------------ */

const mdText = (s: string) => s.replace(/\u00a0/g, ' ').replace(/([*_[\]`\\])/g, '\\$1').replace(/</g, '&lt;');

function inlineMd(node: Node): string {
  let out = '';
  node.childNodes.forEach((n) => {
    if (n.nodeType === 3) { out += mdText(n.textContent ?? ''); return; }
    if (!(n instanceof Element)) return;
    const inner = inlineMd(n);
    switch (n.tagName) {
      case 'STRONG': case 'B': out += inner.trim() ? `**${inner}**` : inner; break;
      case 'EM': case 'I': out += inner.trim() ? `*${inner}*` : inner; break;
      case 'A': out += `[${inner}](${n.getAttribute('href') ?? ''})`; break;
      case 'CODE': out += `\`${n.textContent ?? ''}\``; break;
      case 'IMG': out += `![${n.getAttribute('alt') ?? ''}](${n.getAttribute('src') ?? ''})`; break;
      case 'BR': out += '  \n'; break;
      case 'SPAN': case 'FONT': out += inner; break;
      default: out += n.outerHTML;
    }
  });
  return out;
}

export function htmlToMd(root: Element): string {
  const out: string[] = [];
  let loose = '';
  const flush = () => { if (loose.trim()) out.push(loose.trim()); loose = ''; };
  root.childNodes.forEach((n) => {
    if (n.nodeType === 3) { loose += mdText(n.textContent ?? ''); return; }
    if (!(n instanceof Element)) return;
    const t = n.tagName;
    if (/^(STRONG|B|EM|I|A|CODE|BR|SPAN|IMG)$/.test(t)) { loose += inlineMd({ childNodes: [n] } as unknown as Node); return; }
    flush();
    if (t === 'P' || (t === 'DIV' && !n.attributes.length)) out.push(inlineMd(n).trim());
    else if (/^H[2-4]$/.test(t)) out.push(`${'#'.repeat(Number(t[1]))} ${inlineMd(n).trim()}`);
    else if (t === 'UL' || t === 'OL') out.push([...n.children].map((li, i) => `${t === 'OL' ? `${i + 1}.` : '-'} ${inlineMd(li).trim()}`).join('\n'));
    else if (t === 'BLOCKQUOTE') out.push(n.textContent!.trim().split('\n').map((l) => `> ${mdText(l.trim())}`).join('\n'));
    else out.push(n.outerHTML);
  });
  flush();
  return out.filter((b) => b.trim()).join('\n\n');
}

/* ---- blocks → the editor's HTML ------------------------------------------- */

const short = (name: string) => name.replace(/^.*\//, '');

function sectionHtml(b: Block, i: number): string {
  const at = `data-md-block="${i}"`;
  const list = <T,>(v: unknown) => (Array.isArray(v) ? (v as T[]) : []);
  switch (b._name) {
    case 'case-studies/lede':
      return `<div class="container tc-lede" ${at}>${mdToHtml(str(b.body))}</div>`;
    case 'case-studies/takeaways':
      return `<div class="tc-takeaways" ${at}><h2>${esc(str(b.title))}</h2><ul>${list<Block>(b.items)
        .map((it) => `<li><div class="tc-tag">${esc(str(it.label))}</div>${esc(str(it.text))}</li>`).join('')}</ul></div>`;
    case 'case-studies/facts':
      return `<div class="tc-facts" ${at}><h2 class="tc-facts__title">${esc(str(b.title))}</h2><table><tbody>${list<Block>(b.facts).map((f) => {
        const link = str(f.link_text) && str(f.link_url) ? ` <a href="${escAttr(str(f.link_url))}">${esc(str(f.link_text))}</a>` : '';
        return `<tr><td><strong>${esc(str(f.label))}:</strong></td><td>${esc(str(f.value))}${link}</td></tr>`;
      }).join('')}</tbody></table></div>`;
    case 'case-studies/content-section': {
      const quote = str(b.quote).trim() ? `<blockquote><span itemprop="reviewBody">${esc(str(b.quote).trim())}</span></blockquote>` : '';
      const figure = str(b.image).trim() ? figureHtml(str(b.image), str(b.image_alt), str(b.caption)) : '';
      return `<div class="tc-content" ${at}><h2>${esc(str(b.heading))}</h2>${mdToHtml(str(b.body))}${quote}${figure}</div>`;
    }
    case 'case-studies/cta': {
      const btn = (b.button ?? {}) as Block;
      const nap = [str(b.business_name) && `<strong>${esc(str(b.business_name))}</strong>`, esc(str(b.address)), esc(str(b.phone)),
        str(btn.text) && `<a class="tc-btn" href="${escAttr(str(btn.link))}">${esc(str(btn.text))}</a>`].filter(Boolean).join('<br>');
      return `<div class="tc-cta__inner" ${at}><h2>${esc(str(b.heading))}</h2>${mdToHtml(str(b.body))}${nap ? `<div class="tc-nap">${nap}</div>` : ''}</div>`;
    }
    case 'case-studies/faq':
      return `<div class="tc-faq" ${at}><h2>${esc(str(b.title))}</h2>${list<Block>(b.faqs)
        .map((q) => `<div class="tc-faq__item"><h3>${esc(str(q.question))}</h3><div class="tc-faq__answer">${mdToHtml(str(q.answer))}</div></div>`).join('')}</div>`;
    default: {
      // Photos and anything else: a box the editor stashes and puts back.
      const label = b._name === 'global/gallery-grid' ? `Photo gallery${b.title ? `: ${str(b.title)}` : ''}` : `${short(str(b._name) || 'block')} (edit in Markdown)`;
      return `<div data-tiq="block" data-label="${escAttr(label)}" ${at}></div>`;
    }
  }
}

const figureHtml = (image: string, alt: string, caption: string) =>
  `<div class="tc-figure" data-md-image="${escAttr(image)}" data-md-alt="${escAttr(alt)}" data-md-caption="${escAttr(caption)}"><img src="${escAttr(image)}" alt="${escAttr(alt)}" />${caption ? `<div class="tc-figcaption">${esc(caption)}</div>` : ''}</div>`;

/** The case study drawn as the editor's sections, or null when it is not one. */
export function caseStudyHtml(md: string): string | null {
  const blocks = caseStudyBlocks(md);
  return blocks ? blocks.map(sectionHtml).join('') : null;
}

/* ---- the editor's HTML → blocks ------------------------------------------- */

const text = (el: Element | null | undefined) => (el?.textContent ?? '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();

function take(el: Element, sel: string): Element | null {
  const found = el.querySelector(sel);
  found?.remove();
  return found;
}

function readSection(el: Element, orig: Block): Block {
  const b: Block = structuredClone(orig);
  switch (orig._name) {
    case 'case-studies/lede':
      b.body = htmlToMd(el);
      break;
    case 'case-studies/takeaways': {
      b.title = text(take(el, ':scope > h2'));
      const items = Array.isArray(orig.items) ? (orig.items as Block[]) : [];
      b.items = [...el.querySelectorAll('li')].map((li, i) => {
        const tag = take(li, '.tc-tag');
        return { ...(items[i] ?? {}), label: text(tag), text: text(li) };
      });
      break;
    }
    case 'case-studies/facts': {
      b.title = text(take(el, 'h2'));
      const facts = Array.isArray(orig.facts) ? (orig.facts as Block[]) : [];
      b.facts = [...el.querySelectorAll('tr')].map((tr, i) => {
        const [k, v] = [...tr.querySelectorAll('td')];
        const links = v ? [...v.querySelectorAll('a')] : [];
        const link = links[links.length - 1];
        link?.remove();
        return {
          ...(facts[i] ?? {}), label: text(k).replace(/:\s*$/, ''), value: text(v),
          link_text: link ? text(link) : '', link_url: link?.getAttribute('href') ?? '',
        };
      });
      break;
    }
    case 'case-studies/content-section': {
      b.heading = text(take(el, ':scope > h2'));
      const quote = take(el, ':scope > blockquote');
      b.quote = quote ? text(quote.querySelector('[itemprop="reviewBody"]') ?? quote) : '';
      const fig = take(el, ':scope > .tc-figure');
      b.image = fig?.getAttribute('data-md-image') ?? '';
      b.image_alt = fig?.getAttribute('data-md-alt') ?? '';
      b.caption = fig?.getAttribute('data-md-caption') ?? '';
      // A second photo dragged into the section has no field of its own: it goes in the body.
      const extra = [...el.querySelectorAll(':scope > .tc-figure')].map((f) => {
        f.remove();
        return `![${f.getAttribute('data-md-alt') ?? ''}](${f.getAttribute('data-md-image') ?? ''})`;
      });
      b.body = [htmlToMd(el), ...extra].filter((s) => s.trim()).join('\n\n');
      break;
    }
    case 'case-studies/cta':
      b.heading = text(take(el, ':scope > h2'));
      take(el, ':scope > .tc-nap');
      b.body = htmlToMd(el);
      break;
    case 'case-studies/faq': {
      b.title = text(take(el, ':scope > h2'));
      const faqs = Array.isArray(orig.faqs) ? (orig.faqs as Block[]) : [];
      b.faqs = [...el.querySelectorAll('.tc-faq__item')].map((item, i) => ({
        ...(faqs[i] ?? {}), question: text(item.querySelector('h3')), answer: htmlToMd(item.querySelector('.tc-faq__answer') ?? item),
      }));
      break;
    }
  }
  return b;
}

/** `md` with its content_blocks read back from the edited sections, in their new order. */
export function caseStudyFromHtml(md: string, html: string): string {
  const f = split(md);
  const blocks = caseStudyBlocks(md);
  if (!f || !blocks) return md;
  const page = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const next: Block[] = [];
  for (const el of [...page.body.children]) {
    const i = Number(el.getAttribute('data-md-block'));
    if (!el.hasAttribute('data-md-block') || !blocks[i]) continue;
    next.push(readSection(el, blocks[i]));
  }
  const doc = parseDocument(f.yaml);
  doc.set('content_blocks', doc.createNode(next));
  return `${f.open}${doc.toString({ lineWidth: 0 })}${f.close}${f.rest}`;
}
